import { useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import { Download, X } from "lucide-react";
import { CARD_CLASS } from "../styles";
import type { DiffDashboardColumn, DiffDashboardHotelRow } from "../diffTypes";

type Props = {
  title: string;
  subtitle?: string;
  columns: DiffDashboardColumn[];
  rows: DiffDashboardHotelRow[];
  eyebrow?: string;
  onDownload?: () => void;
  downloadLoading?: boolean;
};

type HoverTooltip = {
  text: string;
  x: number;
  y: number;
};

const HOTEL_COLUMN_WIDTH = 340;
const DATE_COLUMN_WIDTH = 104;

const formatValue = (value: number | null) => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("ru-RU");
};

const formatPriceValue = (value: number | null) => {
  if (value === null || value === undefined || value === 0) return "—";
  return value.toLocaleString("ru-RU");
};

const getRowDeltaBounds = (values: Array<number | null>) => {
  const numeric = values.filter((value): value is number => value !== null && value !== undefined && value !== 0);
  const negativeMin = numeric.filter((value) => value < 0).reduce((min, value) => Math.min(min, value), 0);
  const positiveMax = numeric.filter((value) => value > 0).reduce((max, value) => Math.max(max, value), 0);
  return { negativeMin, positiveMax };
};

const getDeltaCellStyle = (
  value: number | null,
  negativeMin: number,
  positiveMax: number,
): { className: string; style?: CSSProperties } => {
  if (value === null || value === undefined) {
    return { className: "bg-white text-slate-400" };
  }
  if (value === 0) {
    return { className: "bg-white text-slate-600" };
  }
  if (value < 0 && negativeMin < 0) {
    const ratio = Math.min(Math.abs(value / negativeMin), 1);
    const opacity = 0.08 + ratio * 0.38;
    return {
      className: "text-right font-semibold",
      style: {
        backgroundColor: `rgba(244, 63, 94, ${opacity.toFixed(3)})`,
        color: "#881337",
      },
    };
  }
  if (value > 0 && positiveMax > 0) {
    const ratio = Math.min(value / positiveMax, 1);
    const opacity = 0.08 + ratio * 0.38;
    return {
      className: "text-right font-semibold",
      style: {
        backgroundColor: `rgba(34, 197, 94, ${opacity.toFixed(3)})`,
        color: "#166534",
      },
    };
  }
  return { className: "bg-white text-slate-600" };
};

const getDeltaGapTooltip = (delta: number) => {
  if (delta < 0) {
    return "Предложения были раньше, но на сегодня их нет.";
  }
  if (delta > 0) {
    return "Предложение появилось сейчас, ранее его не было.";
  }
  return "";
};

const isDeltaGapState = (priceToday: number | null, delta: number | null) => {
  if (delta === null || delta === undefined || delta === 0) return false;
  if (priceToday === 0) return true;
  if (priceToday !== null && priceToday !== undefined && priceToday === delta) return true;
  return false;
};

export default function DiffDateTablesCard({
  title,
  subtitle,
  columns,
  rows,
  eyebrow = "Тестовый сценарий",
  onDownload,
  downloadLoading = false,
}: Props) {
  const [hoverTooltip, setHoverTooltip] = useState<HoverTooltip | null>(null);

  const handleTooltipMove = (event: ReactMouseEvent<HTMLElement>, text: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    });
  };

  return (
    <section className={CARD_CLASS}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">{eyebrow}</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onDownload && (
              <button
                type="button"
                onClick={onDownload}
                disabled={downloadLoading}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download size={14} />
                {downloadLoading ? "Скачиваем..." : "Скачать XLSX"}
              </button>
            )}
            <div className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
              {columns.length} дат в периоде
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-[28px] border border-slate-200 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
          <div style={{ minWidth: HOTEL_COLUMN_WIDTH + columns.length * DATE_COLUMN_WIDTH }}>
            <DiffTableBlock
              variant="prices"
              title="Минимальные цены по датам"
              columns={columns}
              rows={rows}
              onTooltipMove={handleTooltipMove}
              onTooltipHide={() => setHoverTooltip(null)}
            />
            <div className="h-5 border-y border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50" />
            <DiffTableBlock
              variant="delta"
              title="Дельта к прошлой дате"
              columns={columns}
              rows={rows}
              onTooltipMove={handleTooltipMove}
              onTooltipHide={() => setHoverTooltip(null)}
            />
          </div>
        </div>
      </div>
      {hoverTooltip && (
        <div
          className="pointer-events-none fixed z-[200] max-w-[320px] rounded-xl bg-slate-900/60 px-3 py-2 text-left text-xs font-medium leading-5 text-white shadow-2xl backdrop-blur-sm"
          style={{ left: hoverTooltip.x, top: hoverTooltip.y, transform: "translateX(-50%)" }}
        >
          {hoverTooltip.text}
        </div>
      )}
    </section>
  );
}

function DiffTableBlock({
  variant,
  title,
  columns,
  rows,
  onTooltipMove,
  onTooltipHide,
}: {
  variant: "prices" | "delta";
  title: string;
  columns: DiffDashboardColumn[];
  rows: DiffDashboardHotelRow[];
  onTooltipMove: (event: ReactMouseEvent<HTMLElement>, text: string) => void;
  onTooltipHide: () => void;
}) {
  return (
    <table className="w-full border-collapse text-sm">
      <colgroup>
        <col style={{ width: HOTEL_COLUMN_WIDTH }} />
        {columns.map((column) => (
          <col key={column.isoDate} style={{ width: DATE_COLUMN_WIDTH }} />
        ))}
      </colgroup>
      <thead>
        <tr className="bg-slate-100">
          <th className="sticky left-0 z-20 border border-slate-200 bg-slate-100 px-5 py-4 text-left text-lg font-semibold text-slate-900 shadow-[10px_0_18px_-16px_rgba(15,23,42,0.45)]">
            {title}
          </th>
          {columns.map((column) => (
            <th key={column.isoDate} className="border border-slate-200 bg-slate-100 px-3 py-3 text-center text-sm font-semibold text-slate-900">
              {column.shortLabel}
            </th>
          ))}
        </tr>
        <tr className="bg-indigo-50/70">
          <th className="sticky left-0 z-20 border border-slate-200 bg-indigo-50 px-5 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 shadow-[10px_0_18px_-16px_rgba(15,23,42,0.45)]">
            {variant === "prices" ? "" : "Прошлая дата"}
          </th>
          {columns.map((column) => (
            <th key={`${column.isoDate}-weekday`} className="border border-slate-200 bg-indigo-50/70 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600">
              {column.weekDay}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const bounds = getRowDeltaBounds(row.deltas);
          const stickyBackground = rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50";
          return (
            <tr key={`${variant}-${row.hotelName}`} className={stickyBackground}>
              <td
                className={[
                  "sticky left-0 z-10 border border-slate-200 px-5 py-3 text-left shadow-[10px_0_18px_-16px_rgba(15,23,42,0.45)]",
                  stickyBackground,
                ].join(" ")}
              >
                <div className="font-semibold text-slate-900">{row.hotelName}</div>
              </td>

              {variant === "prices" &&
                row.prices.map((value, index) => (
                  <td
                    key={`${row.hotelName}-price-${columns[index]?.isoDate ?? index}`}
                    className="border border-slate-200 px-3 py-3 text-right font-semibold text-slate-900"
                  >
                    {formatPriceValue(value)}
                  </td>
                ))}

              {variant === "delta" &&
                row.deltas.map((value, index) => {
                  const priceToday = row.prices[index] ?? null;
                  const hasGapState = isDeltaGapState(priceToday, value);
                  const cellStyle = hasGapState
                    ? { className: "bg-white text-slate-500" }
                    : getDeltaCellStyle(value, bounds.negativeMin, bounds.positiveMax);
                  return (
                    <td
                      key={`${row.hotelName}-delta-${columns[index]?.isoDate ?? index}`}
                      className={[
                        "group relative border border-slate-200 px-3 py-3 font-semibold transition-opacity",
                        hasGapState ? "text-right text-slate-800 opacity-50 hover:opacity-100" : "text-right",
                        cellStyle.className,
                      ].join(" ")}
                      style={cellStyle.style}
                      onMouseEnter={(event) => {
                        if (hasGapState && typeof value === "number") {
                          onTooltipMove(event, getDeltaGapTooltip(value));
                        }
                      }}
                      onMouseMove={(event) => {
                        if (hasGapState && typeof value === "number") {
                          onTooltipMove(event, getDeltaGapTooltip(value));
                        }
                      }}
                      onMouseLeave={onTooltipHide}
                    >
                      {hasGapState && typeof value === "number" ? (
                        <>
                          <span className="inline-flex items-center justify-end gap-2" aria-label={getDeltaGapTooltip(value)}>
                            <span className="inline-flex items-center justify-center rounded-full border border-rose-300 bg-rose-100 p-1 text-rose-700">
                              <X size={14} strokeWidth={2.6} />
                            </span>
                            <span className="text-slate-800">{formatValue(value)}</span>
                          </span>
                        </>
                      ) : (
                        formatValue(value)
                      )}
                    </td>
                  );
                })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
